import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Lê os filtros a partir de `URLSearchParams`. Só serve filtros com valores
 * `string` — o formato de todo filtro hoje (texto, select, ou um id vindo de
 * busca de contato). Chave ausente na URL cai no `defaultValue`.
 */
export function readFiltersParams<T extends object>(
  searchParams: URLSearchParams,
  defaultValue: T,
): T {
  const result = { ...defaultValue } as Record<string, string>;
  for (const key of Object.keys(defaultValue)) {
    const raw = searchParams.get(key);
    if (raw !== null) result[key] = raw;
  }
  return result as T;
}

/**
 * Aplica os filtros num `URLSearchParams` mutável — valor == default some da
 * URL. `T` não precisa de index signature (interfaces concretas com campos
 * de união, ex. `status: TransactionStatus | ''`, não têm uma) — todo campo
 * de filtro é `string` em runtime, só o tipo estático é mais específico.
 */
export function writeFiltersParams<T extends object>(
  params: URLSearchParams,
  next: T,
  defaultValue: T,
): void {
  const nextRecord = next as Record<string, string>;
  const defaults = defaultValue as Record<string, string>;
  for (const key of Object.keys(defaultValue)) {
    const nextValue = nextRecord[key];
    if (!nextValue || nextValue === defaults[key]) {
      params.delete(key);
    } else {
      params.set(key, nextValue);
    }
  }
}

/**
 * Filtros de uma lista sincronizados com a URL, no mesmo espírito do
 * `usePageQueryParam` — sobrevivem a "Voltar" (nativo ou `useBackNavigate`)
 * em vez de resetar quando o componente da lista remonta
 * (primum-core-root/docs/plans/done/admin-dashboard-pagination-url-sync.md).
 *
 * **Aplicar filtro normalmente reseta a página para 1** — se sua lista também
 * usa `usePageQueryParam`, NÃO chame os dois setters em sequência no mesmo
 * handler (ver aviso em `use-page-query-param.ts`); monte a mudança num único
 * `setSearchParams` usando `readPageParam`/`writePageParam` +
 * `readFiltersParams`/`writeFiltersParams`. Ver `TotvsSyncTasksPage.tsx`.
 *
 * @param defaultValue filtros vazios — precisa ser uma referência **estável**
 * (constante em module scope), já que identifica "sem filtro" por igualdade.
 */
export function useUrlFilters<T extends object>(
  defaultValue: T,
): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const value = useMemo(
    () => readFiltersParams(searchParams, defaultValue),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams],
  );

  const setValue = (next: T) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writeFiltersParams(params, next, defaultValue);
        return params;
      },
      { replace: true },
    );
  };

  return [value, setValue];
}
